import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { useApiKeysCRUDActions } from '@mm/state/actions/ApiKeysCRUDActions';
import { ApiKeyModelType } from '@mm/state/model/ApiKeyModel';
import modalActions from '@mm/state/modules/modalsState/actions';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { GeneralConfirmModalProps } from '@mm/state/modules/modalsState/props';

// TODO can be used in Zapier Panel
export const useApiKeys = (
  connectionType: ApiKeyModelType,
  apiKeyName: string,
  confirmModalProps: Partial<GeneralConfirmModalProps>,
) => {
  const modalProps = Object.assign(
    { headerText: '', messageText: '' },
    confirmModalProps,
  );

  const { busy, entities: apiKeys } = useSelector(
    (state: StoreState) => state.apiKeys,
  );

  const chatbotId = useSelector(activeChatbotIdSelector);
  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const hasZapierFeature = useSelector(combinedFeatureFlagsSelector).zapier;

  const dispatch: Dispatch<StoreState> = useDispatch();
  const { createKey, deleteKey, getKeys } = useApiKeysCRUDActions();

  const isUpgradeRequired = !busy && !hasZapierFeature;

  useEffect(() => {
    if (activeFacebookPage) {
      getKeys(activeFacebookPage.id);
    }
  }, [chatbotId]);

  // Create an API key if we don't have one
  useEffect(() => {
    if (
      !busy &&
      apiKeys &&
      !apiKey &&
      !isUpgradeRequired &&
      activeFacebookPage
    ) {
      createKey(activeFacebookPage.id, {
        connectionType,
        name: apiKeyName,
      });
    }
  }, [busy, apiKeys, activeFacebookPage, isUpgradeRequired]);

  const apiKeysByType = apiKeys.filter(
    k => k.connectionType === connectionType,
  );

  const apiKey = apiKeysByType.find(k => k.active);

  const confirmRegenerateApiKey = (
    onCloseHandler: (okResult: boolean) => void,
  ) => {
    dispatch(
      modalActions.openGeneralConfirmModal({
        ...modalProps,
        onCloseHandler,
      }),
    );
  };

  const regenerateKey = useCallback(async () => {
    if (!busy && apiKey && activeFacebookPage) {
      confirmRegenerateApiKey(async ok => {
        if (ok) {
          await deleteKey(activeFacebookPage.id, apiKey);
          await createKey(activeFacebookPage.id, {
            connectionType,
            name: apiKeyName,
          });
        }
      });
    }
  }, [chatbotId, apiKey, busy, activeFacebookPage]);

  return {
    apiKeysByType,
    apiKey,
    busy,
    regenerateKey,
    isUpgradeRequired,
  };
};
